import { useCallback, useRef } from 'react';

import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import type { PromoFieldConfig } from '../../../../config/tpoGenConfig';
import type { UpliftConfig } from '../../../../config/upliftConfig';
import type { PPGRow } from '../../../../types/promoMetrics';
import { buildOfferPayload } from '../../../../utils/buildOfferPayload';
import { buildAttributesPayload } from '../../../../utils/entityAttributes';
import { TRANSFORMS } from '../../../../utils/buildPromoPayload';
import type { UpliftRequestPayload, UpliftResponse } from '../mutations/useUpliftMutation';
import { sendNotification } from 'utils/helperMethods';

type TriggerMode = 'manual' | 'auto';

interface UseUpliftTriggerArgs {
    ppgData: PPGRow[];
    promoFormValues: Record<string, unknown>;
    promoType: string | undefined;
    dim2Name: string;
    planId: string;
    promotionId: string | null;
    onMetricChange: (ppgId: string, metricKey: string, newValue: number) => void;
    mutateAsync: (payload: UpliftRequestPayload) => Promise<UpliftResponse>;
    isPending: boolean;
    /** Engine-derived promo field states (used for offer-completeness visibility). */
    fieldStates: Record<string, { visible: boolean }>;
}

interface UseUpliftTriggerReturn {
    triggerUplift: () => Promise<void>;
    triggerUpliftBeforeSave: () => Promise<boolean>;
    triggerUpliftAuto: () => Promise<void>;
    triggerUpliftDebounced: () => void;
    isUpliftApplicable: boolean;
}

/**
 * Hard validation: returns the first metric key that is 0 across any PPG,
 * or null if all required metrics are valid. Used by manual + pre-save paths.
 */
function hasInvalidMetricsForUplift(ppgData: PPGRow[], requiredMetricKeys: string[]): string | null {
    for (const ppg of ppgData) {
        for (const key of requiredMetricKeys) {
            if (Number(ppg.values[key]) === 0) {
                return key;
            }
        }
    }
    return null;
}

/**
 * Soft filter for auto-trigger path: silently removes PPGs where the
 * filter key (edlp_srp) has no value — no error shown to the user.
 */
function filterPpgsForAutoTrigger(ppgData: PPGRow[], filterKey: string): PPGRow[] {
    return ppgData.filter((ppg) => ppg.values[filterKey] && Number(ppg.values[filterKey]) !== 0);
}

/** Extracts only the payload-relevant metrics from each PPG row */
function buildUpliftPpgPayload(ppgData: PPGRow[], payloadMetricKeys: string[]) {
    return ppgData.map((ppg) => {
        const promo_metrics: Record<string, number> = {};
        for (const key of payloadMetricKeys) {
            promo_metrics[key] = ppg.values[key] ?? 0;
        }
        return { dim1_id: ppg.ppg_id, promo_metrics };
    });
}

/**
 * Offer completeness gate: counts expected params for the active sub-method by
 * inspecting offer-param fields that are currently visible (engine-derived), then
 * compares against what buildOfferPayload actually produced.
 */
function isOfferComplete(
    promoFields: PromoFieldConfig[],
    formValues: Record<string, unknown>,
    fieldStates: Record<string, { visible: boolean }>,
): boolean {
    const offerPayload = buildOfferPayload(promoFields, formValues);
    if (!offerPayload) return false;

    let expectedParamCount = 0;

    for (const field of promoFields) {
        if (!field.offerMapping) continue;
        // Only count fields visible for the currently selected sub-method.
        if (!fieldStates[field.name]?.visible) continue;

        const mappings = Array.isArray(field.offerMapping) ? field.offerMapping : [field.offerMapping];
        for (const mapping of mappings) {
            if (mapping.role !== 'param' || !mapping.paramName) continue;
            expectedParamCount++;
            if (mapping.unitParamName) expectedParamCount++;
        }
    }

    return Object.keys(offerPayload.params.params).length >= expectedParamCount;
}

/**
 * Orchestrates uplift validation, payload construction, API trigger, and response
 * consumption. Uses refs for latest args to keep returned functions stable and
 * prevent dependency cycles in consumer effects.
 */
const useUpliftTrigger = ({
    ppgData,
    promoFormValues,
    promoType,
    dim2Name,
    planId,
    promotionId,
    onMetricChange,
    mutateAsync,
    isPending,
    fieldStates,
}: UseUpliftTriggerArgs): UseUpliftTriggerReturn => {
    const { upliftConfig, promoFields, entityAttributes } = useTpoGenConfig();
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Refs to hold latest values — avoids recreating callbacks when data changes.
    // `upliftConfig` / `promoFields` / `entityAttributes` come from context and are
    // threaded through here so the stable callbacks always read the live config.
    const argsRef = useRef({
        ppgData,
        promoFormValues,
        promoType,
        dim2Name,
        planId,
        promotionId,
        onMetricChange,
        mutateAsync,
        isPending,
        upliftConfig,
        promoFields,
        entityAttributes,
        fieldStates,
    });
    argsRef.current = {
        ppgData,
        promoFormValues,
        promoType,
        dim2Name,
        planId,
        promotionId,
        onMetricChange,
        mutateAsync,
        isPending,
        upliftConfig,
        promoFields,
        entityAttributes,
        fieldStates,
    };

    // Gate: only applicable for configured promo types (merchandising / sm_mdf)
    const isUpliftApplicable = !!promoType && upliftConfig.applicablePromotionTypes.includes(promoType);

    /** Sets the target uplift metric per PPG, triggering the KPI cascade engine */
    const applyUpliftResponse = useCallback((data: UpliftResponse) => {
        const { onMetricChange: onChange, upliftConfig: cfg } = argsRef.current;
        for (const item of data.results) {
            onChange(String(item.dim1_id), cfg.targetMetricKey, item.tpo_uplift_percent);
        }
    }, []);

    /** Assembles the full API request payload from PPG data + form values */
    const buildPayload = useCallback((filteredPpgs: PPGRow[]): UpliftRequestPayload | null => {
        const {
            promoFormValues: fv,
            dim2Name: dim2,
            planId: pid,
            promotionId: promoId,
            upliftConfig: cfg,
            promoFields: fields,
            entityAttributes: attrs,
        } = argsRef.current;
        if (filteredPpgs.length === 0) return null;

        const offer = buildOfferPayload(fields, fv);
        if (!offer) return null;

        return {
            plan_id: pid,
            promotion_id: promoId ?? '',
            attributes: buildAttributesPayload(attrs, fields, fv, 'promotion', TRANSFORMS),
            dim2_name: dim2,
            offer_details: offer,
            ppgs: buildUpliftPpgPayload(filteredPpgs, cfg.payloadMetricKeys),
        };
    }, []);

    /**
     * Core trigger with mode-specific validation:
     * - 'manual': hard validation with error notification
     * - 'auto': soft filter (silently skip PPGs without EDLP SRP)
     */
    const triggerUplift = useCallback(
        async (mode: TriggerMode = 'manual') => {
            const {
                ppgData: currentPpgData,
                isPending: pending,
                mutateAsync: mutate,
                promoFormValues: fv,
                upliftConfig: cfg,
                promoFields: fields,
                fieldStates: states,
            } = argsRef.current;
            const applicable =
                !!argsRef.current.promoType && cfg.applicablePromotionTypes.includes(argsRef.current.promoType);

            if (!applicable || currentPpgData.length === 0 || pending) return;

            // Skip if offer params aren't fully populated yet
            if (!isOfferComplete(fields, fv, states)) return;

            let targetPpgs: PPGRow[];

            if (mode === 'auto') {
                targetPpgs = filterPpgsForAutoTrigger(currentPpgData, cfg.autoTriggerFilterKey);
                if (targetPpgs.length === 0) return;
            } else {
                const invalidKey = hasInvalidMetricsForUplift(currentPpgData, cfg.requiredMetricKeys);
                if (invalidKey) {
                    sendNotification('error', `${invalidKey} cannot be 0 for uplift calculation`);
                    return;
                }
                targetPpgs = currentPpgData;
            }

            const payload = buildPayload(targetPpgs);
            if (!payload) return;

            try {
                const response = await mutate(payload);
                applyUpliftResponse(response);
            } catch {
                // Error handled in mutation hook
            }
        },
        [buildPayload, applyUpliftResponse],
    );

    /** Exposed to UI for the manual "Refresh Uplift" button */
    const triggerUpliftManual = useCallback(async () => {
        await triggerUplift('manual');
    }, [triggerUplift]);

    /**
     * Pre-save step: validates, calls API, applies response, then returns
     * true (proceed with save) or false (abort save).
     */
    const triggerUpliftBeforeSave = useCallback(async (): Promise<boolean> => {
        // Cancel any debounced auto-trigger so it can't fire a duplicate call right after save.
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }

        const {
            ppgData: currentPpgData,
            promoFormValues: fv,
            mutateAsync: mutate,
            upliftConfig: cfg,
            promoFields: fields,
            fieldStates: states,
        } = argsRef.current;
        const applicable =
            !!argsRef.current.promoType && upliftConfig.applicablePromotionTypes.includes(argsRef.current.promoType);

        if (!applicable || currentPpgData.length === 0) return true;

        if (!isOfferComplete(fields, fv, states)) return true;

        const invalidKey = hasInvalidMetricsForUplift(currentPpgData, cfg.requiredMetricKeys);
        if (invalidKey) {
            sendNotification('error', `${invalidKey} cannot be 0 for uplift calculation`);
            return false;
        }

        const payload = buildPayload(currentPpgData);
        if (!payload) return true;

        try {
            const response = await mutate(payload);
            applyUpliftResponse(response);
            return true;
        } catch {
            return false;
        }
    }, [buildPayload, applyUpliftResponse]);

    /** Auto-trigger after recommended values fetch (soft filter, no error) */
    const triggerUpliftAuto = useCallback(async () => {
        await triggerUplift('auto');
    }, [triggerUplift]);

    /** Debounced auto-trigger (500ms) for offer field changes */
    const triggerUpliftDebounced = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            triggerUpliftAuto();
        }, 500);
    }, [triggerUpliftAuto]);

    return {
        triggerUplift: triggerUpliftManual,
        triggerUpliftBeforeSave,
        triggerUpliftAuto,
        triggerUpliftDebounced,
        isUpliftApplicable,
    };
};

export default useUpliftTrigger;
